import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  AlertTriangle, 
  Send, 
  Loader2,
  Navigation,
  Zap,
  Users,
  CheckCircle,
  Shield,
  ExternalLink,
  Clock,
  Activity
} from 'lucide-react';
import { Emergency, EmergencyType, SeverityLevel } from '../types';
import { getCurrentLocation, GeolocationResult } from '../utils/geolocation';

interface EmergencyRequestFormProps {
  onSubmit: (emergency: Omit<Emergency, 'id' | 'status' | 'timestamp'>) => void;
  className?: string;
}

export const EmergencyRequestForm: React.FC<EmergencyRequestFormProps> = ({ 
  onSubmit, 
  className = '' 
}) => {
  const [formData, setFormData] = useState({
    type: 'medical' as EmergencyType,
    severity: 'medium' as SeverityLevel,
    description: '',
    peopleAffected: '',
    location: {
      latitude: 0,
      longitude: 0,
      address: '',
    },
  });

  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAiClassifying, setIsAiClassifying] = useState(false);
  const [showAiRecommendations, setShowAiRecommendations] = useState(false);
  const [showBlockchainStatus, setShowBlockchainStatus] = useState(false);

  // Emergency type options as requested
  const emergencyTypes: { value: EmergencyType; label: string }[] = [
    { value: 'fire', label: 'Fire Emergency' },
    { value: 'medical', label: 'Medical Emergency' },
    { value: 'police', label: 'Police Emergency' },
    { value: 'rescue', label: 'Rescue Emergency' },
    { value: 'other', label: 'Natural Disaster' },
    { value: 'accident', label: 'Vehicle Accident' },
  ];

  // Severity levels with emojis as requested
  const severityLevels: { value: SeverityLevel; label: string; color: string; bgColor: string }[] = [
    { value: 'critical', label: '🔴 Critical', color: 'text-red-700', bgColor: 'bg-red-100 border-red-300' },
    { value: 'high', label: '🟠 High', color: 'text-orange-700', bgColor: 'bg-orange-100 border-orange-300' },
    { value: 'medium', label: '🟡 Medium', color: 'text-yellow-700', bgColor: 'bg-yellow-100 border-yellow-300' },
    { value: 'low', label: '🟢 Low', color: 'text-green-700', bgColor: 'bg-green-100 border-green-300' },
  ];

  // Mock AI recommendations
  const aiRecommendations = [
    {
      name: 'Fire Chief Johnson',
      match: '95%',
      distance: '2.3 miles away',
      eta: '6 min ETA',
      skills: ['Fire Chief', 'Emergency Medical'],
      verified: true,
      blockchain: true
    },
    {
      name: 'Dr. Sarah Mitchell',
      match: '92%',
      distance: '1.8 miles away',
      eta: '4 min ETA',
      skills: ['Emergency Medicine', 'Trauma Care'],
      verified: true,
      blockchain: true
    },
    {
      name: 'Officer Martinez',
      match: '88%',
      distance: '3.1 miles away',
      eta: '8 min ETA',
      skills: ['Police', 'Crisis Management'],
      verified: true,
      blockchain: true
    }
  ];

  const handleGetLocation = async () => {
    setIsGettingLocation(true);
    setLocationError(null);

    try {
      const location: GeolocationResult = await getCurrentLocation();
      setFormData(prev => ({
        ...prev,
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          address: `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`,
        },
      }));
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : 'Failed to get location');
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleAiClassify = async () => {
    setIsAiClassifying(true);
    
    // Simulate AI processing
    setTimeout(() => {
      // Auto-classify based on description keywords
      const description = formData.description.toLowerCase();
      if (description.includes('fire') || description.includes('smoke')) {
        setFormData(prev => ({ ...prev, type: 'fire', severity: 'high' }));
      } else if (description.includes('medical') || description.includes('heart') || description.includes('injury')) {
        setFormData(prev => ({ ...prev, type: 'medical', severity: 'critical' }));
      } else if (description.includes('police') || description.includes('crime')) {
        setFormData(prev => ({ ...prev, type: 'police', severity: 'high' }));
      }
      
      setIsAiClassifying(false);
      setShowAiRecommendations(true);
      setShowBlockchainStatus(true);
    }, 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      alert('Please provide a description of the emergency');
      return;
    }

    if (formData.location.latitude === 0 && formData.location.longitude === 0) {
      alert('Please provide your location');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      
      // Reset form
      setFormData({
        type: 'medical',
        severity: 'medium',
        description: '',
        peopleAffected: '',
        location: {
          latitude: 0,
          longitude: 0,
          address: '',
        },
      });
      setShowAiRecommendations(false);
      setShowBlockchainStatus(false);
    } catch (error) {
      console.error('Failed to submit emergency request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-get location on component mount
  useEffect(() => {
    handleGetLocation();
  }, []);

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Main Emergency Form */}
      <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-red-200">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
          <div>
            <h2 className="text-4xl font-bold text-gray-900">Report Emergency</h2>
            <p className="text-xl text-gray-600">Submit your emergency request for immediate assistance</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Emergency Type Dropdown */}
          <div>
            <label className="block text-2xl font-bold text-gray-700 mb-4">
              Type of Emergency
            </label>
            <select
              className="w-full p-6 border-4 border-gray-300 rounded-2xl focus:ring-4 focus:ring-red-500 focus:border-red-500 text-2xl font-medium"
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as EmergencyType }))}
            >
              {emergencyTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Severity Level Radio Buttons */}
          <div>
            <label className="block text-2xl font-bold text-gray-700 mb-4">
              Severity Level
            </label>
            <div className="grid grid-cols-2 gap-4">
              {severityLevels.map(level => (
                <label key={level.value} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="severity"
                    value={level.value}
                    checked={formData.severity === level.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value as SeverityLevel }))}
                    className="w-6 h-6 text-red-600 focus:ring-red-500 mr-4"
                  />
                  <span className={`px-6 py-3 rounded-2xl text-xl font-bold border-2 ${level.bgColor} ${level.color}`}>
                    {level.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-2xl font-bold text-gray-700 mb-4">
              Description of Emergency
            </label>
            <textarea
              className="w-full p-6 border-4 border-gray-300 rounded-2xl focus:ring-4 focus:ring-red-500 focus:border-red-500 text-xl"
              rows={5}
              placeholder="Please provide details about the emergency situation..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          {/* AI Classify Button */}
          <button
            type="button"
            onClick={handleAiClassify}
            disabled={isAiClassifying || !formData.description.trim()}
            className="w-full flex items-center justify-center gap-4 p-6 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-2xl font-bold border-4 border-blue-400"
          >
            {isAiClassifying ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <Zap className="w-8 h-8" />
            )}
            🤖 AI Classify Emergency
          </button>

          {/* Number of People Affected */}
          <div>
            <label className="block text-2xl font-bold text-gray-700 mb-4">
              Number of People Affected
            </label>
            <input
              type="number"
              className="w-full p-6 border-4 border-gray-300 rounded-2xl focus:ring-4 focus:ring-red-500 focus:border-red-500 text-2xl"
              placeholder="Enter number"
              value={formData.peopleAffected}
              onChange={(e) => setFormData(prev => ({ ...prev, peopleAffected: e.target.value }))}
              min="1"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-2xl font-bold text-gray-700 mb-4">
              Location
            </label>
            <div className="space-y-4">
              <button
                type="button"
                onClick={handleGetLocation}
                disabled={isGettingLocation}
                className="w-full flex items-center justify-center gap-4 p-6 bg-purple-600 text-white rounded-2xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-2xl font-bold border-4 border-purple-400"
              >
                {isGettingLocation ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : (
                  <Navigation className="w-8 h-8" />
                )}
                📍 Get Current Location
              </button>

              {locationError && (
                <p className="text-red-600 text-xl font-medium">{locationError}</p>
              )}

              <input
                type="text"
                className="w-full p-6 border-4 border-gray-300 rounded-2xl focus:ring-4 focus:ring-red-500 focus:border-red-500 text-2xl"
                placeholder="Or enter address manually..."
                value={formData.location.address}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  location: { ...prev.location, address: e.target.value }
                }))}
              />

              {(formData.location.latitude !== 0 || formData.location.longitude !== 0) && (
                <div className="flex items-center gap-3 text-xl text-green-600 bg-green-50 p-4 rounded-2xl border-2 border-green-200">
                  <MapPin className="w-6 h-6" />
                  <span className="font-medium">
                    Location detected: {formData.location.latitude.toFixed(4)}, {formData.location.longitude.toFixed(4)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-4 p-8 bg-red-600 text-white rounded-2xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-3xl font-bold border-4 border-red-400"
          >
            {isSubmitting ? (
              <Loader2 className="w-10 h-10 animate-spin" />
            ) : (
              <Send className="w-10 h-10" />
            )}
            🚨 SUBMIT EMERGENCY REQUEST
          </button>
        </form>
      </div>

      {/* AI Recommended Matches Section */}
      {showAiRecommendations && (
        <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-green-200">
          <h3 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-4">
            <Zap className="w-10 h-10 text-green-600" />
            🤖 AI Recommended Matches
          </h3>
          
          <div className="space-y-6">
            {aiRecommendations.map((responder, index) => (
              <div key={index} className="border-4 border-gray-200 rounded-2xl p-6 hover:border-green-300 transition-colors bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-bold text-gray-800">{responder.name}</h4>
                  <div className="flex gap-3">
                    {responder.verified && (
                      <span className="bg-green-600 text-white px-4 py-2 rounded-full text-lg font-bold">
                        Verified ✓
                      </span>
                    )}
                    {responder.blockchain && (
                      <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-lg font-bold">
                        Blockchain ⛓️
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-6 mb-4">
                  <div className="text-center bg-white p-4 rounded-xl border-2 border-green-200">
                    <div className="text-3xl font-bold text-green-600">{responder.match}</div>
                    <div className="text-lg text-gray-600 font-medium">Match</div>
                  </div>
                  <div className="text-center bg-white p-4 rounded-xl border-2 border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{responder.distance}</div>
                    <div className="text-lg text-gray-600 font-medium">Distance</div>
                  </div>
                  <div className="text-center bg-white p-4 rounded-xl border-2 border-orange-200">
                    <div className="text-2xl font-bold text-orange-600">{responder.eta}</div>
                    <div className="text-lg text-gray-600 font-medium">ETA</div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  {responder.skills.map((skill, skillIndex) => (
                    <span key={skillIndex} className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-lg font-medium border-2 border-blue-200">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            
            <button className="w-full flex items-center justify-center gap-4 p-6 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-colors text-2xl font-bold border-4 border-red-400">
              <CheckCircle className="w-8 h-8" />
              ✅ Accept AI Recommendations
            </button>
          </div>
        </div>
      )}

      {/* Blockchain Status Panel */}
      {showBlockchainStatus && (
        <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-purple-200">
          <h3 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-4">
            <Shield className="w-10 h-10 text-purple-600" />
            ⛓️ Blockchain Verification Status
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-6 bg-green-50 rounded-2xl border-2 border-green-200">
              <span className="text-xl font-bold text-gray-800">Emergency Logged to Blockchain:</span>
              <span className="text-green-600 font-bold text-xl">✅ Confirmed</span>
            </div>
            
            <div className="flex items-center justify-between p-6 bg-blue-50 rounded-2xl border-2 border-blue-200">
              <span className="text-xl font-bold text-gray-800">Transaction Hash:</span>
              <span className="text-blue-600 font-mono text-lg">#ALG847592</span>
            </div>
            
            <div className="flex items-center justify-between p-6 bg-green-50 rounded-2xl border-2 border-green-200">
              <span className="text-xl font-bold text-gray-800">Responder Credentials:</span>
              <span className="text-green-600 font-bold text-xl">✅ Verified</span>
            </div>
            
            <div className="flex items-center justify-between p-6 bg-yellow-50 rounded-2xl border-2 border-yellow-200">
              <span className="text-xl font-bold text-gray-800">Response Time Recording:</span>
              <span className="text-yellow-600 font-bold text-xl">⏱️ Active</span>
            </div>
            
            <button className="w-full flex items-center justify-center gap-4 p-6 bg-purple-600 text-white rounded-2xl hover:bg-purple-700 transition-colors text-2xl font-bold border-4 border-purple-400">
              <ExternalLink className="w-8 h-8" />
              🔍 View on Algorand Explorer
            </button>
          </div>
        </div>
      )}

      {/* Real-Time Status Board */}
      <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-indigo-200">
        <h3 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-4">
          <Activity className="w-10 h-10 text-indigo-600" />
          📊 Live Emergency Status
        </h3>
        
        <div className="space-y-6">
          {/* Emergency #E001 */}
          <div className="border-4 border-gray-200 rounded-2xl p-6 bg-red-50">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-2xl font-bold text-gray-800">Emergency #E001 - House Fire</h4>
              <span className="text-gray-600 font-medium text-lg">(3 min ago)</span>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-lg font-medium text-gray-700 mb-3">
                <span>Reported</span>
                <span>Assigned</span>
                <span>En Route</span>
                <span>On Scene</span>
                <span>Resolved</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6 border-2 border-gray-300">
                <div className="bg-orange-500 h-6 rounded-full transition-all duration-500" style={{ width: '60%' }}></div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xl font-medium text-gray-700">
                <strong>Assigned to:</strong> Fire Chief Johnson
              </span>
              <span className="bg-orange-100 text-orange-800 px-6 py-3 rounded-full font-bold text-lg border-2 border-orange-200">
                ETA: 3 min
              </span>
            </div>
          </div>

          {/* Emergency #E002 */}
          <div className="border-4 border-gray-200 rounded-2xl p-6 bg-blue-50">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-2xl font-bold text-gray-800">Emergency #E002 - Medical Emergency</h4>
              <span className="text-gray-600 font-medium text-lg">(8 min ago)</span>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-lg font-medium text-gray-700 mb-3">
                <span>Reported</span>
                <span>Assigned</span>
                <span>En Route</span>
                <span>On Scene</span>
                <span>Resolved</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6 border-2 border-gray-300">
                <div className="bg-purple-500 h-6 rounded-full transition-all duration-500" style={{ width: '80%' }}></div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xl font-medium text-gray-700">
                <strong>Assigned to:</strong> Dr. Sarah Mitchell
              </span>
              <span className="bg-purple-100 text-purple-800 px-6 py-3 rounded-full font-bold text-lg border-2 border-purple-200">
                ETA: Arrived
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};